import classNames from 'classnames'
import React, { useEffect, useState } from 'react'

import PaymentsBalance from '../elements/dashboard/PaymentsBalance'
import Link from '../elements/Link'
import { ExternalLinkIcon } from '../svgs/DashboardSVGs'

import { getMyAccountMenuItems } from '../../constants/menuItems'
import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards'
import { useGetCurrentUserProfile } from '../../hooks/apolloHooks/useGetCurrentUserProfile'
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer'
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY, GET_BASIC_CUSTOMER_DATA_QUERY, GET_CONTACT_CUSTOMER_DATA_QUERY, GET_CUSTOMER_AGREEMENTS_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries'
import { BankCardsCustomerDataResponse, BasicCustomerDataResponse, ContactDataResponse, CustomerAgreementsResponse } from '../../integrations/allkort/graphql/interface/Customer'
import { TokenService } from '../../services/TokenService'
import { EMAIL_TYPES } from '../../utils/filterEmailsByType'
import { PHONE_TYPE_IDS } from '../../utils/filterPhonesByType'
import FuelStats from '../elements/dashboard/FuelStats'
import Information from '../elements/dashboard/Information'
import ProfileInfo from '../elements/dashboard/ProfileInfo'
import QstarServices from '../elements/dashboard/QstarServices'
import QuickLinks from '../elements/dashboard/QuickLinks'
import ErrorDisplay from '../elements/ErrorDisplay'
import FetchingAnimation from '../elements/FetchingAnimation'
import SecondMenu from '../elements/SecondMenu'
import { CalendarMonthIcon, LightBulbIcon, MobileIcon, StoreIcon, WalletIcon } from '../svgs/ExpressonSVGs'
import UpdateAccountInformation from '../views/UpdateAccountInformation'

// DashboardPage fetches its own data - no props needed
const DashboardPage: React.FC = () => {

	// Track if this is the first dashboard visit in the current session
	const [isFirstDashboardVisit, setIsFirstDashboardVisit] = useState(true);

	useEffect(() => {
		const hasVisitedBefore = sessionStorage.getItem('qstar_dashboard_first_visit');
		if (!hasVisitedBefore) {
			// First time visiting dashboard in this session
			setIsFirstDashboardVisit(true);
			sessionStorage.setItem('qstar_dashboard_first_visit', 'true');
		} else {
			// Returning to dashboard after visiting another page
			setIsFirstDashboardVisit(false);
		}

		// Log current token for testing in Apollo UI
		// TODO: Remove this after testing, or replace with proper token management and error handling if token is missing/expired
		const token = TokenService.getToken();
		if (token) {
			//console.log('[DASHBOARD] Current Access Token for Apollo UI:');
			//console.log(token);
		} else {
			console.log('[DASHBOARD] No active token found');
		}
	}, []);

	// Always fetch data - no props needed
	const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		undefined
	);

	const { data: customerContactData, loading: customerContactLoading, error: customerContactError } = useGetCustomer<ContactDataResponse>(
		GET_CONTACT_CUSTOMER_DATA_QUERY,
		undefined,
		{ fetchPolicy: 'network-only' }
	);

	// Fetch ALL cards with automatic pagination (up to 200 cards)
	const { cards: allCards, loading: cardsLoading, error: cardsError } = useGetAllCards();

	const { userProfile } = useGetCurrentUserProfile();
	const isSuperUser = (userProfile?.roles ?? []).some(r => r.claimName === 'Superuser');

	// Fetch bank cards data with same pagination as regular cards
	const bankCardsVariables = React.useMemo(() => ({ first: null, after: null, before: null, last: 49 }), []);

	const { data: bankCardsData, loading: bankCardsLoading, error: bankCardsError } = useGetCustomer<BankCardsCustomerDataResponse>(
		GET_BANKCARDS_CUSTOMER_DATA_QUERY,
		bankCardsVariables
	);

	// Get agreements to check if customer has approved the app agreement
	const BATCH_SIZE = 15; // Number of agreements to fetch per request
	const { data: agreementsData, loading: agreementsLoading, error: agreementsError } = useGetCustomer<CustomerAgreementsResponse>(
		GET_CUSTOMER_AGREEMENTS_QUERY,
		{ first: BATCH_SIZE, after: null, before: null, last: null }
	);
	const hasAppAgreement = agreementsData?.currentCustomer?.agreementApprovals?.nodes?.some(
		approval => approval.agreement.id === 354 // 354 is the id for the agreement related to the app agreement, this is hardcoded until we have a better way to identify it
	) ?? false;

	// Filter cards by type (P = prepaid/smartcards, K = klubkort/tankkort/creditcards)
	// Only count cards that are NOT disabled
	const hasActiveSmartCard = allCards.some((card: any) => card.type?.shortName === 'P' && card.limit?.disabled?.isDisabled !== true);
	const hasActiveCreditCard = allCards.some((card: any) => card.type?.shortName === 'K' && card.limit?.disabled?.isDisabled !== true);
	const hasBankCards = (bankCardsData?.currentCustomer?.bankCards?.nodes?.length ?? 0) > 0;

	// Count cards by type
	const smartCardCount = allCards.filter((card: any) => card.type?.shortName === 'P').length;
	const creditCardCount = allCards.filter((card: any) => card.type?.shortName === 'K').length;
	const bankCardCount = bankCardsData?.currentCustomer?.bankCards?.nodes?.length ?? 0;

	// Check if there's only one card of each type
	const oneSmartCard = smartCardCount === 1;
	const oneCreditCard = creditCardCount === 1;
	const oneBankCard = bankCardCount === 1;


	// Active card refers to Smartcard or Creditcard, that can be disabled
	const hasAtLeastOneActiveCard = hasActiveSmartCard || hasActiveCreditCard;

	// Prettify name, sometimes it is capitalized
	const prettyfyName = (name: string) => {
		if (!name) {
			return 'Profile name'
		}
		const newName = name
			.split(' ')
			.map(
				(singleName) =>
				(singleName =
					singleName.charAt(0).toUpperCase() +
					singleName.slice(1).toLowerCase())
			)
			.join(' ')
		return newName
	}

	let paymentBalanceHeadingText = 'Aktuella betalningar & saldo'
	if (!hasActiveSmartCard) {
		paymentBalanceHeadingText = 'Aktuella betalningar'
	}
	if (!hasActiveCreditCard) {
		paymentBalanceHeadingText = 'Aktuellt saldo'
	}

	let dashboardWrapperClasses = ''
	if (!hasActiveSmartCard && !hasActiveCreditCard) {
		dashboardWrapperClasses = 'no-qstar-cards'
	}

	/* Skeleton loading animation */
	if (customerLoading || cardsLoading || bankCardsLoading || customerContactLoading) {
		return (
			<div className="skeleton-dashboard-page">
				{/* this anumates the secondmenu component */}
				<FetchingAnimation width={644} maxWidth={true} height={151} marginBottom={14} marginTop={0} />
				<div className="skeleton-page-content-wrapper">
					<FetchingAnimation width={'100'} maxWidth={true} height={240} marginBottom={10} marginTop={15} />
					<FetchingAnimation width={'100'} maxWidth={true} height={700} marginBottom={10} marginTop={32} />
				</div>
			</div>);
	}

	const isCompany = customerBasicData?.currentCustomer?.isCompany ?? false;

	// Check if customer has verified MAIN email and phone — only show UpdateAccountInformation if missing
	const hasVerifiedMainEmail = customerContactData?.currentCustomer?.emails?.some(
		e => e.type.id === EMAIL_TYPES.MAIN_EMAIL && e.verified
	) ?? false
	const hasVerifiedMainPhone = customerContactData?.currentCustomer?.phoneNumbers?.some(
		p => p.type?.id === PHONE_TYPE_IDS.MAIN && p.verified
	) ?? false
	const needsAccountUpdate = !hasVerifiedMainEmail || !hasVerifiedMainPhone


	// --- Email check ---
	const mainEmails = customerContactData?.currentCustomer?.emails?.filter(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL)

	// Generate menu items dynamically based on customer's card types
	const menuItems = getMyAccountMenuItems((smartCardCount > 0), (creditCardCount > 0));

	const readMoreQstarCards = isCompany
		? 'https://qstar.se/foretagskort/'
		: 'https://qstar.se/medlem/'


	// Handle errors
	if (customerError) return <ErrorDisplay errorContext="Fel vid laddning av kunddata:" errorMessage={customerError?.message} />
	if (cardsError) return <ErrorDisplay errorContext="Fel vid laddning av kort(dashboard):" errorMessage={cardsError?.message} />
	if (bankCardsError) return <ErrorDisplay errorContext="Fel vid laddning av bankkort:" errorMessage={bankCardsError?.message} />
	if (customerContactError) return <ErrorDisplay errorContext="Fel vid laddning av kontaktuppgifter:" errorMessage={customerContactError?.message} />

	const today = new Date();
	const isPromoDate = today.getFullYear() === 2026 && today.getMonth() === 5 && (today.getDate() === 6 || today.getDate() === 7); // Show promotion on June 6-7, 2026
	const storeOfferText = isPromoDate
		? <>Du får just nu ytterligare 20% rabatt på redan nedsatta priser på ett urval av produkter när du använder kod <strong>6JUNI</strong> i Qstars förmånsbutik under 6-7 juni.</>
		: <>Här hittar du allt för utflykten, köket, hemmet och mycket mer.</>;
	const storeOfferHeading = isPromoDate
		? <>Förmånsbutiken</>
		: 'Mer för pengarna i vår förmånsbutik';
	const storeOfferLink = isPromoDate ? 'https://formansbutik.qstar.se/dina-erbjudanden?pl=medlem56356' : 'https://formansbutik.qstar.se/?pl=medlem56356';
	return (
		<>
			{/* Second menu displaying, dashboard, settings, invoices/statements and user management */}
			<SecondMenu menuItems={menuItems} />

			{/* Dashboard */}
			<div className="Section dashboard-body dashboard-page-component">
				<div className="Row Row--JustifyLeft">

					{/* Dashboard header */}
					<div className={classNames(
						'Row__Inner',
						'dashboard-header',
						'white-pale-section'
						, 'shadow-one',
					)} >
						<div className="left">
							<span title="Meta" aria-label='Dagens datum' className="dashboard-header__date">
								<CalendarMonthIcon width={22} height={22} />
								{new Date().toLocaleDateString('sv-SE', { day: '2-digit', month: 'long', year: 'numeric' })}
							</span>
							<h1 className="dashboard-header__title">
								{isFirstDashboardVisit ? 'Välkommen tillbaka,' : 'Välkommen,'}<br />
								{prettyfyName(`${customerBasicData!.currentCustomer!.firstName} ${customerBasicData!.currentCustomer!.lastName}`)}
							</h1>
							<p className="dashboard-header__subtitle">
								Här är en överblick över ditt konto.
							</p>
						</div>

						{/* Only show this if there is at least one active card */}
						{hasAtLeastOneActiveCard ? (
							<div className={classNames(
								"right"
							)}>
								<span className="meta">
									<WalletIcon width={22} height={22} />
									{paymentBalanceHeadingText}
								</span>

								<PaymentsBalance
									customerData={customerBasicData!.currentCustomer!}
									hasActiveSmartCard={hasActiveSmartCard}
									hasActiveCreditCard={hasActiveCreditCard}
								/>
							</div>
						) : (
							<div className={classNames(
								"right no-services"
							)}>
								<span className="meta" role="heading" aria-level={5}>
									<LightBulbIcon width={28} height={28} />
									Qstar och våra tjänster
								</span>
								<div className="content">
									<p>
										Du har just nu ingen av våra korttjänster. Med appen kan du enkelt betala på våra stationer. Vi erbjuder både {isCompany ? 'företagskort ' : 'tankkort '} där du kan samla alla dina köp på en faktura eller Smartkort där du laddar pengar som kan ge dig mer kontroll över din förbrukning och kostnad.
									</p>
									<Link href={readMoreQstarCards} className="dashboard-button no-margin" openNewTab={true}>
										Läs mer om våra tjänster
										<ExternalLinkIcon width={20} height={20} />
									</Link>
								</div>

							</div>
						)}
					</div>
					{/* --- End dashboard header --- */}

					{/* New dashboard content */}
					<div className={classNames(
						'Row__Inner',
						'dashboard-content white-pale-section shadow-one',
					)} >
						<FuelStats
							isCompany={isCompany}
							hasCreditcard={hasActiveCreditCard}
							hasSmartcard={hasActiveSmartCard}
							hasBankcard={hasBankCards}
						/>


						<QstarServices
							className="qstar-services-component"
							hasCreditcard={hasActiveCreditCard}
							hasSmartcard={hasActiveSmartCard}
							hasBankcard={hasBankCards}
							isCompany={isCompany}
						/>

						<ProfileInfo
							customerData={customerBasicData!.currentCustomer!}
							mainEmail={mainEmails && mainEmails.length > 0 ? mainEmails[0].address : 'Ingen e-post registrerad'}
						/>

						<QuickLinks
							hasAtLeastOneActiveCard={hasAtLeastOneActiveCard}
							hasSmartCard={hasActiveSmartCard}
						/>


						<Information
							heading={storeOfferHeading}
							uspText='Medlemsförmån'
							uspColor='black'
							backgroundIcon={<StoreIcon className="" width={145} height={145} style={{ rotate: '0deg', opacity: 0.65, fill: '#277a5a', position: 'absolute', right: '-10px', bottom: '-20px' }} />}
						>
							<p className="no-flex">{storeOfferText}</p>
							<Link
								className="dashboard-button"
								ariaLabel={'Extern förmånsbutik'}
								href={storeOfferLink}
								openNewTab={true}
							>
								{'Förmånsbutik'}
								<ExternalLinkIcon />
							</Link>
						</Information>

						{!hasAppAgreement && (
							<Information
								heading='Ladda ner vår app'
								uspText='Tips'
								uspColor=''
								backgroundColorClass='black'
								backgroundIcon={<MobileIcon className="snowflake" style={{ rotate: '20deg', opacity: 0.2, position: 'absolute', right: '-30px', bottom: '-20px' }} width={145} height={145} />}
							>
								<p>Visste du att du kan betala med vår app på stationerna? Välj pump och betala smidigt direkt i mobilen.</p>
								<br />
								<p><WalletIcon width={22} height={22} />Betala med bankkort, qstarkort, Apple Pay eller Google Pay</p>
								<Link
									className="dashboard-button"
									ariaLabel={'Testa vår app'}
									href={'https://qstar.se/app/'}
									openNewTab={true}
								>
									{'Läs mer'}
									<ExternalLinkIcon />
								</Link>
							</Information>
						)}

					</div>
				</div>

				{/* Force main email and main phone update — only shown to Superusers */}
				{needsAccountUpdate && isSuperUser && (
					<UpdateAccountInformation
						customerContactData={customerContactData}
						customerContactLoading={customerContactLoading}
						customerContactError={customerContactError}
						accountId={customerBasicData?.currentCustomer?.id}
					/>
				)}
			</div>
		</>
	)
}
export default DashboardPage
